import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';

function formatDate(ts) {
  try { return new Date(ts || Date.now()).toLocaleString(); }
  catch { return ''; }
}

function Row({ item, inputName, onSaved, onRemoved }) {
  const { t } = useTranslation();
  const [name, setName]   = useState(item.name ?? '');
  const [input, setInput] = useState(item.input ?? item.content ?? '');
  const [busySave, setBusySave] = useState(false);
  const [busyDelete, setBusyDelete] = useState(false);

  useEffect(() => { setName(item.name ?? ''); }, [item.name]);
  useEffect(() => { setInput(item.input ?? item.content ?? ''); }, [item.input, item.content]);

  const dirty = (
    name  !== (item.name   ?? '') ||
    input !== (item.input ?? item.content ?? '')
  );

  async function handleSave() {
    if (!dirty) return;
    try {
      setBusySave(true);
      const { data } = await api.put(`/content-items/${item._id}`, {
        name,
        input,
        content: input, // keep both in sync
      });
      const updated = data?.item ?? { ...item, name, input, content: input, updatedAt: Date.now() };
      onSaved?.(updated);
    } catch (e) {
      window.alert(e?.response?.data?.msg || t('errors.saveFailed'));
    } finally {
      setBusySave(false);
    }
  }

  async function handleRemove() {
    if (!window.confirm(t('editor.confirmDelete'))) return;
    try {
      setBusyDelete(true);
      await api.delete(`/content-items/${item._id}`);
      onRemoved?.(item._id);
    } catch (e) {
      window.alert(e?.response?.data?.msg || t('errors.removeFailed'));
    } finally {
      setBusyDelete(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey || !e.shiftKey)) {
      e.preventDefault();
      handleSave();
    }
  }

  return (
    <tr id={item._id} className="hover:bg-gray-50">
      <td className="p-2 border">
        <input
          className="w-full border border-gray-300 rounded-md py-1.5 px-3"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onKeyDown}
        />
      </td>
      <td className="p-2 border">
        <input
          className="w-full border border-gray-300 rounded-md py-1.5 px-3"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={inputName}
        />
      </td>
      <td className="p-2 border whitespace-nowrap align-middle">{formatDate(item.updatedAt)}</td>
      <td className="p-2 border whitespace-nowrap align-middle">
        <button
          className="text-blue-700 underline mr-3 disabled:opacity-50"
          onClick={handleSave}
          disabled={!dirty || busySave}
          title={dirty ? t('editor.actions.saveChanges') : t('editor.actions.noChanges')}
        >
          {busySave ? t('editor.actions.saving') : t('editor.actions.save')}
        </button>
        <button
          className="text-red-700 underline disabled:opacity-50"
          onClick={handleRemove}
          disabled={busyDelete}
        >
          {busyDelete ? t('editor.actions.removing') : t('editor.actions.delete')}
        </button>
      </td>
    </tr>
  );
}

export default function DoubleInputForm({ sectionId, inputName, reloadKey }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const displayInputName = inputName || t('editor.value');

  // fetch minimal list, then hydrate each row to get "input"
  async function fetchItems() {
    try {
      setLoading(true);
      setErr('');

      // 1) list (no 'input' here by design)
      const { data } = await api.get(`/content-items/by-content/${sectionId}`);
      const base = Array.isArray(data) ? data : [];

      // 2) hydrate each row with full item (has input/content)
      const hydrated = await Promise.all(base.map(async (r) => {
        try {
          const { data: full } = await api.get(
            `/content-items/resolve/${sectionId}/${r.link}`
          );
          return { ...r, input: full?.input ?? full?.content ?? '', content: full?.content ?? '' };
        } catch {
          // if resolve fails, fall back to blank (or keep any existing fields)
          return { ...r, input: r.input ?? r.content ?? '' };
        }
      }));

      setRows(hydrated);
    } catch {
      setErr(t('errors.failedToLoadItems'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (sectionId) fetchItems(); }, [sectionId]);
  useEffect(() => { if (sectionId) fetchItems(); }, [reloadKey]); // refresh after create

  function handleRowSaved(updated) {
    setRows(prev => prev.map(r => (r._id === updated._id ? { ...r, ...updated } : r)));
  }
  function handleRowRemoved(id) {
    setRows(prev => prev.filter(r => r._id !== id));
  }

  if (loading) return <div>{t('general.loading')}</div>;
  if (err) return <div className="text-red-600">{err}</div>;

  return (
    <div className="overflow-auto">
      <table className="min-w-[600px] w-full border">
        <thead className="bg-gray-100">
          <tr>
            <th className="text-left p-2 border">{t('editor.table.name')}</th>
            <th className="text-left p-2 border">{displayInputName}</th>
            <th className="text-left p-2 border">{t('editor.table.updated')}</th>
            <th className="text-left p-2 border">{t('editor.table.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).length ? (
            rows.map((i) => (
              <Row
                key={i._id}
                item={i}
                inputName={displayInputName}
                onSaved={handleRowSaved}
                onRemoved={handleRowRemoved}
              />
            ))
          ) : (
            <tr>
              <td className="p-4 text-gray-500" colSpan={4}>
                {t('editor.empty.noItems')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="text-sm text-gray-500 mt-2">
        {t('editor.hint.addWithNew')}
      </p>
    </div>
  );
}
